import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { Avatar, Drawer } from '@pepper/ui'

import { useGetWorkflowLog } from 'utils/hooks/workflows/useGetWorkflowLog'
import AssigneeDropdown from 'components/workflows/AssigneeDropdown'
import StatusDropdown from '../StatusDropdown'

import s from './DocStatusLog.module.css'

type Props = {
  companyId: string
  item: Item | Assignment
  openDrawer: boolean
  setItem: (item: Item) => void
  onDrawerClose: () => void
  assigneeList?: TeamMembers[]
  statusList?: WorkflowStatus[]
}

export const DocStatusLog = ({
  companyId,
  item,
  openDrawer,
  setItem,
  onDrawerClose,
  assigneeList,
  statusList,
}: Props) => {
  const { data: workflowActivities } = useGetWorkflowLog({
    itemId: item.id,
    isPvtDoc: true,
  })

  const creationTime = formatDistanceToNow(new Date(item.created_at), {
    addSuffix: true,
  })
  const workflowStatus = statusList?.find(
    status => status.id === item.workflow_status_id
  )
  const assigneeData = assigneeList
    ?.filter(_assignee => _assignee.id)
    ?.find(assignee => assignee.id === item.workflow_assignee_id)

  return (
    <Drawer
      visible={openDrawer}
      title={
        <div className={s.drawerHeader}>
          <div>{item.title}</div>
        </div>
      }
      width="47%"
      closeIcon={<Icon icon="feather:x" width={20} height={20} />}
      onClose={onDrawerClose}
    >
      <div className={s.workflowMeta}>
        <StatusDropdown
          companyId={companyId}
          statusList={statusList}
          itemStatusId={item.workflow_status_id}
          itemId={item.id}
          isPrivateDoc={true}
        >
          <div className={s.status}>
            <div
              className={clsx(s.statusColor, {
                [s.noColor]: workflowStatus?.name === 'No Status',
              })}
              style={{
                backgroundColor: `#${workflowStatus?.color}`,
                outline: `3px solid #${workflowStatus?.color}25`,
              }}
            />
            {workflowStatus?.name}
            <Icon icon="icon-park-solid:down-one" className={s.downIcon} />
          </div>
        </StatusDropdown>
        <AssigneeDropdown
          companyId={companyId}
          assigneeList={assigneeList}
          itemId={item.id}
          assigneeId={item.workflow_assignee_id}
          isPrivateDoc={true}
          showTooltip={true}
        >
          <div className={s.assignee} role="none">
            {!assigneeData ? (
              <div className={s.unassigned}>
                <Icon icon="lucide:user" height={16} />
              </div>
            ) : assigneeData?.avatar ? (
              <Avatar src={assigneeData?.avatar} className={s.assigneeAvatar} />
            ) : (
              <Avatar className={s.assigneeAvatar}>
                {assigneeData.full_name[0]}
              </Avatar>
            )}
            {assigneeData?.full_name ?? 'No Assignee'}
            <Icon icon="icon-park-solid:down-one" className={s.downIcon} />
          </div>
        </AssigneeDropdown>
      </div>
      <div className={s.tabs}>
        <div className={s.statusLog}>Status Log</div>
      </div>
      <div className={s.workflowHistory}>
        <div className={s.activityItem}>
          {item.added_by?.avatar ? (
            <Avatar src={item.added_by?.avatar} size="small" />
          ) : (
            <Avatar size="small" className={s.logAvatar}>
              {item.added_by?.first_name?.[0]}
            </Avatar>
          )}
          <span className={s.name}>
            {item.added_by?.first_name} {item.added_by?.last_name}
          </span>
          <span className={s.action}>created this item</span>
          <div className={s.bullet} />
          <span className={s.time}>{creationTime}</span>
        </div>
        {Number(workflowActivities?.length) > 0 && <div className={s.line} />}
        {workflowActivities?.map((activity, idx) => {
          const actionTime = formatDistanceToNow(
            new Date(activity.created_at),
            { addSuffix: true }
          )
          if (activity.key === 'workflow_status_id') {
            return (
              <div key={activity.created_at}>
                <div className={s.activityItem}>
                  <div
                    className={s.statusColor}
                    style={{
                      background: `#${activity.status?.current.color}`,
                      outline: `3px solid #${activity.status?.current.color}25`,
                    }}
                  />
                  <span className={s.name}>{activity.updated_by.name}</span>
                  <span className={s.action}>changed status to</span>
                  <span className={s.name}>
                    {activity.status?.current.name}
                  </span>
                  <div className={s.bullet} />
                  <span className={s.time}>{actionTime}</span>
                </div>
                {idx !== workflowActivities?.length - 1 && (
                  <div className={s.line} />
                )}
              </div>
            )
          } else {
            return (
              <div key={activity.created_at}>
                <div className={s.activityItem}>
                  {activity.updated_by.avatar ? (
                    <Avatar src={activity.updated_by.avatar} size="small" />
                  ) : (
                    <Avatar size="small" className={s.logAvatar}>
                      {activity.assignee?.current?.name?.[0]}
                    </Avatar>
                  )}
                  <span className={s.name}>{activity.updated_by.name}</span>
                  <span className={s.action}>re-assigned to</span>
                  <span className={s.name}>
                    {activity.assignee?.current.name ?? 'No Assignee'}{' '}
                    {activity.assignee?.current.isDeleted &&
                      activity.assignee?.current.name &&
                      '(Deleted)'}
                  </span>
                  <div className={s.bullet} />
                  <span className={s.time}>{actionTime}</span>
                </div>
                {idx !== workflowActivities?.length - 1 && (
                  <div className={s.line} />
                )}
              </div>
            )
          }
        })}
      </div>
    </Drawer>
  )
}
